import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '../types';

const detectTenantId = async (): Promise<string | null> => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  if (hostname.includes('.attendai.io')) {
    const subdomain = hostname.split('.')[0];
    const { data } = await supabase
      .from('tenants')
      .select('id')
      .eq('subdomain', subdomain)
      .maybeSingle();
    return data?.id ?? null;
  }

  const { data } = await supabase
    .from('tenants')
    .select('id')
    .eq('custom_domain', hostname)
    .maybeSingle();
  return data?.id ?? null;
};

const buildUserFromSession = async (authUser: { id: string; email?: string }): Promise<User | null> => {
  const email = authUser.email || '';

  const tenantId = await detectTenantId();

  let query = supabase
    .from('tenant_users')
    .select('role, name, tenant_id, tenants(name)')
    .eq('auth_user_id', authUser.id);

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data: tenantUser } = await query.maybeSingle();

  if (!tenantUser) {
    return null;
  }

  const tenantName = (tenantUser.tenants as any)?.name ?? '';

  return {
    id: authUser.id,
    name: tenantUser.name || email.split('@')[0],
    email,
    role: tenantUser.role as User['role'],
    tenantId: tenantUser.tenant_id,
    tenantName
  };
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
          if (isMounted) setLoading(false);
          return;
        }

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          if (sessionError.message?.includes('Invalid Refresh Token')) {
            try { await supabase.auth.signOut(); } catch {}
            if (isMounted) {
              setUser(null);
              setError('Session expired. Please log in again.');
              setLoading(false);
            }
            return;
          }
          if (isMounted) setLoading(false);
          return;
        }

        if (!isMounted) return;

        if (session?.user) {
          const profile = await buildUserFromSession(session.user);
          if (isMounted) {
            if (profile) {
              setUser(profile);
            } else {
              setError('Your account is not assigned to any hospital. Please contact your administrator.');
            }
            setLoading(false);
          }
        } else {
          if (isMounted) setLoading(false);
        }
      } catch (err: any) {
        console.error('Auth initialization error:', err);
        if (isMounted) setLoading(false);
      }
    };

    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      setTimeout(() => { if (isMounted) setLoading(false); }, 1000);
      return () => { isMounted = false; };
    }

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === 'SIGNED_IN' && session?.user) {
        setError(null);
        (async () => {
          const profile = await buildUserFromSession(session.user);
          if (!isMounted) return;
          if (profile) {
            setUser(profile);
          } else {
            setError('Your account is not assigned to any hospital. Please contact your administrator.');
            await supabase.auth.signOut();
          }
          setLoading(false);
        })();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setError(null);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setError(null);
      const { data, error: loginError } = await supabase.auth.signInWithPassword({ email, password });

      if (loginError) {
        setError(`Login failed: ${loginError.message}`);
        return false;
      }

      if (data.user) {
        const profile = await buildUserFromSession(data.user);
        if (!profile) {
          await supabase.auth.signOut();
          setError('Your account is not assigned to any hospital. Please contact your administrator.');
          return false;
        }
      }

      return true;
    } catch (err: any) {
      setError(`Login failed: ${err.message}`);
      return false;
    }
  };

  const logout = async () => {
    setError(null);
    if (!user) { setUser(null); return; }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setUser(null); return; }
    } catch {
      setUser(null);
      return;
    }

    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        if (error.message?.includes('Session from session_id claim') || error.message?.includes('Auth session missing')) {
          setUser(null);
        } else {
          setError(`Logout failed: ${error.message}`);
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
  };

  return { user, login, logout, loading, error };
};
